import React, { FC } from 'react'
import cn from 'clsx'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import Image from 'next/image'
import Trans from 'next-translate/Trans'
import { Logo } from '@components/ui'
import Bitpay from '@components/icons/Bitpay'
import { sendEvent } from '@lib/tracking'
import { FooterIconLink } from '@components/common/Footer'
import { socialMedia } from '@components/constants'
import MasterCardIcon from '@components/icons/MasterCardIcon'
import VisaIcon from '@components/icons/VisaIcon'
import { AmexIcon } from '@components/icons/AmexIcon'
import { ThreeDSecureIcon } from '@components/icons/ThreeDSecureIcon'

interface FooterTextLinkInfo {
  text: string
  target?: string
  href: string
  id: string
}

interface FooterProps {
  className?: string
  customHelpLinks?: FooterTextLinkInfo[]
  customLegalLinks?: FooterTextLinkInfo[]
  handleFooterClick?: typeof defaultHandleFooterClick
}

const defaultHandleFooterClick = (
  clickText: string,
  clickUrl: string,
  buttonId: string
): void => {
  sendEvent({
    event: 'Click_Footer',
    data: {
      button_id: buttonId,
      click_text: clickText,
      click_url: clickUrl,
      page_path: window.location.pathname,
    },
  })
}

const isTriggerKey = (keycode: string) =>
  keycode === 'Space' || keycode === 'Enter'

const Footer: FC<FooterProps> = ({
  className,
  customHelpLinks,
  customLegalLinks,
  handleFooterClick = defaultHandleFooterClick,
}) => {
  const { t } = useTranslation()

  const defaultHelpLinks = [
    {
      text: t('footer:litepaper_link'),
      href: `${
        process.env.NEXT_PUBLIC_VENDURE_HOST ?? ''
      }/assets/litepaper.pdf`,
      id: 'footer_link_litepaper',
      target: '_blank',
    },
    {
      text: t('footer:contact_link'),
      href: '/contact',
      id: 'footer_link_contact',
    },
    {
      text: t('footer:faq_link'),
      href: '/faq',
      id: 'footer_link_faq',
    },
  ]

  const helpLinks = customHelpLinks ?? defaultHelpLinks

  const defaultLegalLinks = [
    {
      text: t('footer:terms_link'),
      href: '/terms',
      id: 'footer_link_terms',
    },
    {
      text: t('footer:privacy_link'),
      href: '/privacy',
      id: 'footer_link_privacy',
    },
  ]

  const legalLinks = customLegalLinks ?? defaultLegalLinks

  const categories = [
    {
      title: t('footer:help_title'),
      items: (
        <div className="flex flex-col items-start space-y-3">
          {helpLinks.map(({ text, href, id, target }) => (
            <Link
              key={text}
              href={href}
              onClick={() => handleFooterClick(text, href, id)}
              target={target}
            >
              {text}
            </Link>
          ))}
        </div>
      ),
    },
    {
      title: t('footer:legal_title'),
      items: (
        <div className="flex flex-col items-start space-y-3">
          {legalLinks.map(({ text, href, id }) => (
            <Link
              key={text}
              href={href}
              onClick={() => handleFooterClick(text, href, id)}
            >
              {text}
            </Link>
          ))}
        </div>
      ),
    },
    {
      title: t('footer:social_title'),
      items: (
        <div className="flex flex-col items-start space-y-3">
          <div className="flex space-x-3 pt-1.5">
            {Object.values(socialMedia).map(({ name, href, icon }) => (
              <FooterIconLink
                key={href}
                href={href}
                onClick={() =>
                  handleFooterClick(
                    name,
                    href,
                    `footer_icon_${name.split(' ')[0]}`
                  )
                }
                elem={icon}
              />
            ))}
          </div>
          <span className="opacity-70">
            <Trans
              i18nKey={'footer:social_text'}
              components={[
                <Link
                  className="underline underline-offset-4"
                  key={0}
                  href="https://knxt-space.com/"
                  target="_blank"
                  onClick={() =>
                    handleFooterClick(
                      t('footer:social_text'),
                      'https://knxt-space.com/',
                      'footer_link_knxt_space'
                    )
                  }
                />,
              ]}
            />
          </span>
        </div>
      ),
    },
    {
      title: null,
      items: (
        <div className="flex">
          <Bitpay className="mr-4" />
          <div className="relative w-14">
            <ThreeDSecureIcon className="absolute bottom-2 right-2" />
          </div>
          <MasterCardIcon className="mr-2" />
          <VisaIcon className="mr-2" />
          <AmexIcon />
        </div>
      ),
    },
  ]

  return (
    <footer
      className={cn(
        'flex flex-col border-t-2 border-deepPurple bg-center relative overflow-clip',
        className
      )}
    >
      {/* TODO : remove unoptimized and make the image not so blurry */}
      <Image
        src="/layout/footerBackground.png"
        fill
        alt="footer-background"
        className="object-cover object-center"
        unoptimized
      />
      <div className="max-w-content-xl mx-content-margin-desktop mb-12 flex flex-col items-center sm:block z-10">
        <div>
          <div className="mr-auto mt-10 mb-6">
            <Link
              href="/"
              role="button"
              tabIndex={0}
              aria-label="Logo"
              onClick={() => handleFooterClick('knxt logo', '/', 'footer_logo')}
              onKeyDown={(event: React.KeyboardEvent) =>
                isTriggerKey(event.code) &&
                handleFooterClick('knxt logo', '/', 'footer_logo')
              }
            >
              <Logo />
            </Link>
          </div>
          <div
            className={cn(
              'flex flex-col justify-between lg:flex-row lg:justify-start',
              className
            )}
          >
            {categories.map((category) => (
              <div
                key={category.title}
                className="flex flex-col mt-8 mb-2 space-y-3 lg:mr-28"
              >
                <p className="uppercase font-bold">{category.title}</p>
                {category.items}
              </div>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
